import type { VariantProps } from 'class-variance-authority'
import type { Ref } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'

import { cn } from '../../lib/utils'

const buttonVariants = cva(
    'inline-flex items-center justify-center whitespace-nowrap rounded-md text-base font-semibold ring-offset-background transition-colors focus-visible:outline-2 focus-visible:outline-transparent focus-visible:shadow-[0_0_0_3px_white,0_0_0_6px_#0537FF] disabled:pointer-events-none disabled:opacity-50 w-full',
    {
        variants: {
            variant: {
                default:
                    'text-base bg-lp-peach1 text-black hover:bg-lp-peach1/90 font-semibold uppercase tracking-normal leading-5 uppercase shadow-[0_2px_4px_rgba(0,0,0,0.08)]',
                destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                outline:
                    'text-base border border-input bg-white hover:bg-accent hover:text-accent-foreground shadow-base',
                secondary:
                    'bg-white text-black border-[1px] border-black font-semibold uppercase tracking-normal leading-5 hover:bg-gray-50 shadow-[0_2px_4px_rgba(0,0,0,0.08)]',
                ghost: 'hover:bg-accent hover:text-accent-foreground text-base',
                link: 'text-primary underline-offset-4 hover:underline',
            },
            size: {
                default: 'h-12 px-4 py-2',
                sm: 'h-9 rounded-md px-3',
                lg: 'h-11 rounded-md px-8',
                icon: 'h-10 w-10',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean
    testId?: string
    isLoading?: boolean
    children?: React.ReactNode
    ref?: Ref<HTMLButtonElement>
}

function Button({
    className,
    variant,
    testId,
    isLoading,
    children,
    size,
    asChild = false,
    ref,
    ...props
}: ButtonProps) {
    const Comp = asChild ? Slot : 'button'
    return (
        <Comp
            data-testid={testId ?? (props.type === 'submit' ? 'button-submit' : 'button')}
            className={cn(buttonVariants({ variant, size, className }))}
            ref={ref}
            {...props}
            disabled={!!props.disabled || isLoading}
            aria-busy={isLoading}
        >
            {isLoading ? <Loader2 className="w-12 animate-spin" aria-hidden="true" /> : children}
        </Comp>
    )
}

export { Button, buttonVariants }
